/**
 * Create / edit one investment program, in two steps: the form, then the list of changes with a
 * reminder that saving changes live recommendations. Only the second step writes.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  Multiselect,
  RadioGroup,
  Select,
  SpaceBetween,
  Table,
  Textarea,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';

import {
  CreateInvestmentProgramCommand,
  getCpaClient,
  type InvestmentProgramData,
  UpdateInvestmentProgramCommand,
} from '../../client/CpaClient';
import {
  diffSettings,
  EMPTY_TIER,
  type FieldChange,
  type FormErrors,
  fromProgram,
  type ProgramForm,
  saveErrorOf,
  toSettings,
} from '../../utils/programForm';
import type { InvestmentProgramSettings } from '../../client/CpaClient';

const PPA_TYPES = [
  ['non_ppa', 'ppaNonPpa'],
  ['sce_led_cross_service', 'ppaSceLedCrossService'],
  ['seller_led_cross_service', 'ppaSellerLedCrossService'],
  ['sce_led_service_specific', 'ppaSceLedServiceSpecific'],
  ['seller_led_service_specific', 'ppaSellerLedServiceSpecific'],
  ['r_ppa', 'ppaRPpa'],
] as const;
const CYCLES = [
  ['recurring', 'cycleRecurring'],
  ['one_time', 'cycleOneTime'],
] as const;
const DISCOUNT_TYPES = [
  ['ratio', 'discountRatio'],
  ['amount', 'discountAmount'],
  ['table', 'discountTable'],
  ['no_limit', 'noLimit'],
] as const;

const labelOf = (pairs: readonly (readonly [string, string])[], value: string) =>
  pairs.find(([v]) => v === value)?.[1];

export default function ProgramEditor({
  program,
  allPrograms,
  onClose,
  onSaved,
}: {
  /** The program to edit; absent to create one. */
  program?: InvestmentProgramData;
  allPrograms: InvestmentProgramData[];
  onClose: () => void;
  onSaved: (saved: InvestmentProgramData) => void;
}) {
  const { t } = useTranslation('cpa');
  // For keys picked at runtime (enum labels, error keys); the static ones keep their checked types.
  const tx = t as unknown as (key: string) => string;
  const isNew = !program;
  const [form, setForm] = useState<ProgramForm>(() => fromProgram(program));
  const [errors, setErrors] = useState<FormErrors>({});
  const [confirming, setConfirming] = useState<{
    settings: InvestmentProgramSettings;
    changes: FieldChange[];
  }>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{ key: string; detail?: string }>();
  // One idempotency token per create attempt, so a retried save returns the program it already made.
  const [clientToken] = useState(() => crypto.randomUUID());

  const set = <K extends keyof ProgramForm>(key: K, value: ProgramForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const err = (key: keyof ProgramForm) => (errors[key] ? tx(errors[key]!) : undefined);

  const others = useMemo(
    () =>
      allPrograms
        .filter((p) => p.programId !== program?.programId && p.name)
        .map((p) => ({ value: p.name!, label: p.name! }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allPrograms, program],
  );
  const picked = (names: string[]) => names.map((n) => ({ value: n, label: n }));
  const options = (pairs: readonly (readonly [string, string])[]) =>
    pairs.map(([value, key]) => ({ value, label: tx(key) }));
  const show = (kind: 'ppaType' | 'discountCycle' | 'discountType', value: string) => {
    const pairs = kind === 'ppaType' ? PPA_TYPES : kind === 'discountCycle' ? CYCLES : DISCOUNT_TYPES;
    const key = labelOf(pairs, value);
    return key ? tx(key) : value;
  };

  const review = () => {
    const { settings, errors: found } = toSettings(form);
    setErrors(found);
    setSaveError(undefined);
    if (!settings) return;
    setConfirming({ settings, changes: diffSettings(program, settings, show) });
  };

  const save = () => {
    if (!confirming) return;
    setSaving(true);
    setSaveError(undefined);
    const request = isNew
      ? getCpaClient().send(new CreateInvestmentProgramCommand({ program: confirming.settings, clientToken }))
      : getCpaClient().send(
          new UpdateInvestmentProgramCommand({
            programId: program!.programId,
            program: confirming.settings,
            expectedUpdatedAt: program!.updatedAt,
          }),
        );
    request
      .then((out) => onSaved(out.program!))
      .catch((e: unknown) => setSaveError(saveErrorOf(e)))
      .finally(() => setSaving(false));
  };

  const header = isNew ? t('editorCreateTitle') : t('editorEditTitle', { name: program!.name });

  if (confirming) {
    return (
      <Modal
        visible
        size="large"
        onDismiss={onClose}
        header={header}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirming(undefined)} disabled={saving}>
                {t('editorBack')}
              </Button>
              <Button
                variant="primary"
                onClick={save}
                loading={saving}
                disabled={!isNew && confirming.changes.length === 0}
              >
                {t('editorConfirmSave')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning">{t(isNew ? 'editorLiveWarningNew' : 'editorLiveWarning')}</Alert>
          {saveError && (
            <Alert type="error" header={tx(saveError.key)}>
              {saveError.detail}
            </Alert>
          )}
          {confirming.changes.length === 0 ? (
            <Box color="text-body-secondary">{t('editorNoChanges')}</Box>
          ) : (
            <Table
              variant="embedded"
              wrapLines
              items={confirming.changes}
              columnDefinitions={[
                { id: 'field', header: t('editorField'), cell: (c) => tx(`diff_${c.field}`), width: 160 },
                { id: 'before', header: t('editorBefore'), cell: (c) => c.before || '-' },
                { id: 'after', header: t('editorAfter'), cell: (c) => <strong>{c.after || '-'}</strong> },
              ]}
            />
          )}
        </SpaceBetween>
      </Modal>
    );
  }

  return (
    <Modal
      visible
      size="max"
      onDismiss={onClose}
      header={header}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={review}>
              {t('editorReview')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h3">{t('editorBasics')}</Header>}>
          <ColumnLayout columns={2}>
            <FormField
              label={t('colName')}
              errorText={err('name')}
              constraintText={isNew ? t('editorNameFixed') : t('editorNameLocked')}
            >
              <Input
                value={form.name}
                disabled={!isNew}
                onChange={({ detail }) => set('name', detail.value)}
              />
            </FormField>
            <FormField
              label={t('colPriority')}
              errorText={err('priority')}
              constraintText={t('editorPriorityHint')}
            >
              <Input
                type="number"
                value={form.priority}
                onChange={({ detail }) => set('priority', detail.value)}
              />
            </FormField>
            <FormField label={t('colType')}>
              <Select
                selectedOption={options(PPA_TYPES).find((o) => o.value === form.ppaType) ?? null}
                options={options(PPA_TYPES)}
                onChange={({ detail }) => set('ppaType', detail.selectedOption.value!)}
              />
            </FormField>
            <FormField label={t('colCycle')}>
              <RadioGroup
                value={form.discountCycle}
                items={options(CYCLES)}
                onChange={({ detail }) => set('discountCycle', detail.value)}
              />
            </FormField>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h3">{t('editorDiscount')}</Header>}>
          <SpaceBetween size="m">
            <RadioGroup
              value={form.discountType}
              items={options(DISCOUNT_TYPES)}
              onChange={({ detail }) => set('discountType', detail.value)}
            />
            {form.discountType === 'ratio' && (
              <ColumnLayout columns={2}>
                <FormField label={t('editorMinRatio')} errorText={err('minRatio')}>
                  <Input value={form.minRatio} onChange={({ detail }) => set('minRatio', detail.value)} />
                </FormField>
                <FormField label={t('editorMaxRatio')} errorText={err('maxRatio')}>
                  <Input value={form.maxRatio} onChange={({ detail }) => set('maxRatio', detail.value)} />
                </FormField>
              </ColumnLayout>
            )}
            {form.discountType === 'amount' && (
              <ColumnLayout columns={2}>
                <FormField label={t('editorMinAmount')} errorText={err('minAmount')}>
                  <Input value={form.minAmount} onChange={({ detail }) => set('minAmount', detail.value)} />
                </FormField>
                <FormField label={t('editorMaxAmount')} errorText={err('maxAmount')}>
                  <Input value={form.maxAmount} onChange={({ detail }) => set('maxAmount', detail.value)} />
                </FormField>
              </ColumnLayout>
            )}
            {form.discountType === 'table' && (
              <FormField
                label={t('editorTiers')}
                errorText={err('tiers')}
                constraintText={t('editorTiersHint')}
              >
                <SpaceBetween size="xs">
                  {form.tiers.map((tier, i) => (
                    <ColumnLayout key={i} columns={4}>
                      <Input
                        ariaLabel={t('editorTierMin')}
                        placeholder={t('editorTierMin')}
                        value={tier.minArr}
                        onChange={({ detail }) =>
                          set(
                            'tiers',
                            form.tiers.map((x, j) => (j === i ? { ...x, minArr: detail.value } : x)),
                          )
                        }
                      />
                      <Input
                        ariaLabel={t('editorTierMax')}
                        placeholder={t('editorTierMax')}
                        value={tier.maxArr}
                        onChange={({ detail }) =>
                          set(
                            'tiers',
                            form.tiers.map((x, j) => (j === i ? { ...x, maxArr: detail.value } : x)),
                          )
                        }
                      />
                      <Input
                        ariaLabel={t('editorTierRatios')}
                        placeholder={t('editorTierRatios')}
                        value={tier.ratios}
                        onChange={({ detail }) =>
                          set(
                            'tiers',
                            form.tiers.map((x, j) => (j === i ? { ...x, ratios: detail.value } : x)),
                          )
                        }
                      />
                      <Button
                        iconName="remove"
                        variant="icon"
                        ariaLabel={t('editorRemoveTier')}
                        onClick={() =>
                          set(
                            'tiers',
                            form.tiers.filter((_, j) => j !== i),
                          )
                        }
                      />
                    </ColumnLayout>
                  ))}
                  <Button
                    iconName="add-plus"
                    onClick={() => set('tiers', [...form.tiers, { ...EMPTY_TIER }])}
                  >
                    {t('editorAddTier')}
                  </Button>
                </SpaceBetween>
              </FormField>
            )}
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h3">{t('editorEligibility')}</Header>}>
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField label={t('editorQualifierMin')} errorText={err('qualifierMin')}>
                <Input
                  value={form.qualifierMin}
                  onChange={({ detail }) => set('qualifierMin', detail.value)}
                />
              </FormField>
              <FormField label={t('editorQualifierMax')} errorText={err('qualifierMax')}>
                <Input
                  value={form.qualifierMax}
                  onChange={({ detail }) => set('qualifierMax', detail.value)}
                />
              </FormField>
            </ColumnLayout>
            <ColumnLayout columns={2}>
              <FormField label={t('editorExclusiveGlobal')} constraintText={t('editorExclusiveHint')}>
                <Multiselect
                  selectedOptions={picked(form.exclusiveGlobal)}
                  options={others}
                  filteringType="auto"
                  onChange={({ detail }) =>
                    set(
                      'exclusiveGlobal',
                      detail.selectedOptions.map((o) => o.value!),
                    )
                  }
                />
              </FormField>
              <FormField label={t('editorExclusiveChina')}>
                <Multiselect
                  selectedOptions={picked(form.exclusiveChina)}
                  options={others}
                  filteringType="auto"
                  onChange={({ detail }) =>
                    set(
                      'exclusiveChina',
                      detail.selectedOptions.map((o) => o.value!),
                    )
                  }
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h3">{t('editorContacts')}</Header>}>
          <ColumnLayout columns={2}>
            <FormField label={t('colPoc')} constraintText={t('editorOnePerLine')}>
              <Textarea value={form.contacts} onChange={({ detail }) => set('contacts', detail.value)} />
            </FormField>
            <FormField label={t('colSla')}>
              <Input value={form.sla} onChange={({ detail }) => set('sla', detail.value)} />
            </FormField>
            <FormField label={t('linkWiki')} errorText={err('wikis')} constraintText={t('editorOnePerLine')}>
              <Textarea value={form.wikis} onChange={({ detail }) => set('wikis', detail.value)} />
            </FormField>
            <FormField
              label={t('linkApply')}
              errorText={err('applicationLinks')}
              constraintText={t('editorOnePerLine')}
            >
              <Textarea
                value={form.applicationLinks}
                onChange={({ detail }) => set('applicationLinks', detail.value)}
              />
            </FormField>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    </Modal>
  );
}
